import { QueryStrings } from '@/config/references'
import { IDataStore } from '@/services/dataStore/IDataStore'
import { retryTimeouts } from '@/services/dataStore/retryTimeouts'
import useDataStore from '@/services/dataStore/useDataStore'
import { useReferencesStore } from '@/store/references'
import { BaseRemedyReferenceSchema, IBaseRemedyReference } from '@lifk/types'
import useSWRV from 'swrv'
import { computed, ref, watch } from 'vue'

interface GetRemedySWRVKey {
  name: string
  remedyKey?: string
}
const currentRouteSWRVKey = ref<GetRemedySWRVKey>({
  name: 'remedy'
})

const useGetRemedy = () => {
  const {
    dataStore,
    dataStoreSWRVKey,
    handleDataStoreError,
    disableValidations
  } = useDataStore()
  const referencesStore = useReferencesStore()

  const remedyKey = computed(() =>
    referencesStore.selectedContentType === QueryStrings.remedy
      ? referencesStore.queryValues?.contentKey
      : undefined
  )
  currentRouteSWRVKey.value.remedyKey = remedyKey.value

  async function fetchRemedy(
    dataStore: IDataStore,
    remedyKey: string
  ): Promise<IBaseRemedyReference | undefined> {
    try {
      const response = await retryTimeouts(() => dataStore.getRemedy(remedyKey))
      if (disableValidations.value) return response
      return BaseRemedyReferenceSchema.parse(response)
    } catch (e) {
      handleDataStoreError(e, 'remedy')
      throw e
    }
  }

  const result = useSWRV(
    () =>
      `/${currentRouteSWRVKey.value.name}/${dataStoreSWRVKey.value.perspective}/${currentRouteSWRVKey.value.remedyKey}`,
    async () => {
      if (currentRouteSWRVKey.value.remedyKey) {
        return await fetchRemedy(
          dataStore.value,
          currentRouteSWRVKey.value.remedyKey
        )
      } else {
        return undefined
      }
    },
    {
      // 30s for adapters to prevent too many API calls to paid CMS's
      dedupingInterval: 30000,
      shouldRetryOnError: false
    }
  )

  watch(remedyKey, () => {
    currentRouteSWRVKey.value.remedyKey = remedyKey.value
  })

  return result
}

export { useGetRemedy }
